import { useState, useEffect } from 'react';
import api from '../../services/api.js';

function formatDuration(s) {
  if (s < 60) return `${s}s`;
  return `${Math.floor(s/60)}min ${s%60}s`;
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024*1024) return `${Math.round(bytes/1024)} Ko`;
  return `${(bytes/(1024*1024)).toFixed(1)} Mo`;
}

export default function RecordingsPage() {
  const [devices, setDevices]     = useState([]);
  const [selected, setSelected]   = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [playUrl, setPlayUrl]     = useState(null);

  useEffect(() => {
    api.get('/devices').then(r => {
      const devs = r.data.data || [];
      setDevices(devs);
      if (devs.length > 0) setSelected(devs[0]);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/recordings/device/${selected.id}?page=${page}&limit=12`)
      .then(r => {
        setRecordings(r.data.data?.recordings || []);
        setTotalPages(r.data.data?.pages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selected, page]);

  return (
    <div>
      <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', marginBottom: 20 }}>
        📼 Mes enregistrements
      </h1>

      {/* Sélecteur de caméra */}
      {devices.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {devices.map(d => (
            <button key={d.id} onClick={() => { setSelected(d); setPage(1); }} style={{
              padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: selected?.id === d.id ? 'var(--elvyra-accent)' : 'var(--elvyra-surface-2)',
              color: selected?.id === d.id ? '#060E1F' : 'var(--elvyra-text-secondary)',
            }}>
              📹 {d.name}
            </button>
          ))}
        </div>
      )}

      {/* Grille des enregistrements */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" style={{ width: 40, height: 40 }} />
        </div>
      ) : recordings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--elvyra-text-muted)' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>📼</div>
          <p style={{ fontSize: 16, marginBottom: 8 }}>Aucun enregistrement</p>
          <p style={{ fontSize: 13 }}>
            Les enregistrements apparaissent automatiquement lors des alertes mouvement (plans PRO/BUSINESS),
            ou peuvent être déclenchés manuellement.
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
            {recordings.map(r => (
              <div key={r.id} style={{ background: 'var(--elvyra-surface)', border: '1px solid var(--elvyra-border)', borderRadius: 12, overflow: 'hidden' }}>
                {/* Thumbnail */}
                <div style={{ background: '#0A1628', aspectRatio: '16/9', position: 'relative', cursor: 'pointer' }} onClick={() => r.url && setPlayUrl(r.url)}>
                  {r.thumbnail ? (
                    <img src={r.thumbnail} alt="capture" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 32 }}>🎥</span>
                    </div>
                  )}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                    <span style={{ fontSize: 40, color: '#fff' }}>▶</span>
                  </div>
                  <span style={{ position: 'absolute', bottom: 6, right: 8, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, padding: '2px 7px', borderRadius: 4 }}>
                    {formatDuration(r.duration)}
                  </span>
                </div>

                {/* Infos */}
                <div style={{ padding: '10px 12px' }}>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--elvyra-text-secondary)' }}>
                    {new Date(r.startedAt).toLocaleString('fr-FR')}
                  </p>
                  <p style={{ margin: '2px 0 8px', fontSize: 11, color: 'var(--elvyra-text-muted)' }}>
                    {formatSize(r.size)}
                  </p>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noreferrer" style={{ flex: 1, display: 'block', textAlign: 'center', padding: '6px', borderRadius: 6, background: 'rgba(0,212,255,0.1)', color: 'var(--elvyra-accent)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                        ⬇ Télécharger
                      </a>
                    )}
                    <button onClick={() => r.url && setPlayUrl(r.url)} style={{ flex: 1, padding: '6px', borderRadius: 6, border: '1px solid var(--elvyra-border)', background: 'transparent', color: 'var(--elvyra-text-secondary)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                      ▶ Lire
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--elvyra-border)', background: 'transparent', color: 'var(--elvyra-text-secondary)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>← Précédent</button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: 'var(--elvyra-text-muted)' }}>Page {page}/{totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--elvyra-border)', background: 'transparent', color: 'var(--elvyra-text-secondary)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.4 : 1 }}>Suivant →</button>
            </div>
          )}
        </>
      )}

      {/* Modal lecteur vidéo */}
      {playUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setPlayUrl(null)}>
          <div style={{ maxWidth: 800, width: '100%' }} onClick={e => e.stopPropagation()}>
            <video src={playUrl} controls autoPlay style={{ width: '100%', borderRadius: 12, maxHeight: '80vh' }} />
            <button onClick={() => setPlayUrl(null)} style={{ display: 'block', margin: '12px auto 0', padding: '8px 24px', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
